import { useState, useEffect } from "react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, addMonths, subMonths, getDay, addDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ChevronLeft, ChevronRight, Download, Trash2, Calendar as CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { ApprovalPost } from "@/types/approval";
import { toast } from "@/lib/toast";
import { ApprovalDetailsDialog } from "./components/ApprovalDetailsDialog";
import { DeletedApprovalsDialog } from "./components/DeletedApprovalsDialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

interface Client {
  id: string;
  name: string;
}

interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  approvals: ApprovalPost[];
}

export default function NewCalendar() {
  const isMobile = useIsMobile();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedClient, setSelectedClient] = useState<string>("all");
  const [clients, setClients] = useState<Client[]>([]);
  const [calendarDays, setCalendarDays] = useState<CalendarDay[]>([]);
  const [approvals, setApprovals] = useState<ApprovalPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedApproval, setSelectedApproval] = useState<ApprovalPost | null>(null);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const [isDeletedDialogOpen, setIsDeletedDialogOpen] = useState(false);

  // Carregar clientes
  useEffect(() => {
    const loadClients = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data, error } = await supabase
          .from('clients')
          .select('id, name')
          .eq('user_id', user.id)
          .eq('status', 'active')
          .order('name');

        if (error) throw error;
        setClients(data || []);
      } catch (error) {
        console.error('Erro ao carregar clientes:', error);
      }
    };

    loadClients();
  }, []);

  // Carregar aprovações do mês
  useEffect(() => {
    const loadApprovals = async () => {
      try {
        setLoading(true);
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        let targetCompanyId: string | null = null;

        // Verificar se é colaborador
        const { data: collaboratorData } = await supabase
          .from('collaborators')
          .select('company_id, permissions')
          .eq('collaborator_id', user.id)
          .eq('status', 'active')
          .maybeSingle();

        if (collaboratorData) {
          targetCompanyId = collaboratorData.company_id;
          const permissions = collaboratorData.permissions as any;
          if (!permissions?.approvals) {
            setApprovals([]);
            return;
          }
        } else {
          const { data: userData } = await supabase
            .from('users')
            .select('company_id')
            .eq('id', user.id)
            .single();
          targetCompanyId = userData?.company_id || null;
        }

        if (!targetCompanyId) {
          setApprovals([]);
          return;
        }

        const monthStart = startOfMonth(currentDate);
        const monthEnd = endOfMonth(currentDate);

        let query = supabase
          .from('approval_posts')
          .select('*')
          .eq('company_id', targetCompanyId)
          .is('deleted_at', null)
          .not('post_date', 'is', null)
          .gte('post_date', monthStart.toISOString())
          .lte('post_date', monthEnd.toISOString())
          .order('post_date');

        if (selectedClient !== "all") {
          query = query.eq('client_name', clients.find(c => c.id === selectedClient)?.name || '');
        }

        const { data, error } = await query;
        if (error) throw error;

        const transformedApprovals: ApprovalPost[] = (data || []).map(approval => ({
          ...approval,
          status: approval.status as 'pending' | 'approved' | 'rejected' | 'archived',
          post_type: (approval.post_type || 'static') as 'static' | 'carousel' | 'reels',
          visible_to: Array.isArray(approval.visible_to) ? approval.visible_to.map(id => String(id)) : []
        }));

        const visibleApprovals = transformedApprovals.filter(approval => {
          if (approval.user_id === user.id) return true;
          if (approval.visible_to && Array.isArray(approval.visible_to)) {
            return approval.visible_to.includes(user.id);
          }
          return collaboratorData ? true : false;
        });

        setApprovals(visibleApprovals);
      } catch (error) {
        console.error('Erro ao carregar aprovações:', error);
        toast.error('Erro ao carregar dados do calendário');
      } finally {
        setLoading(false);
      }
    };

    loadApprovals();
  }, [currentDate, selectedClient, clients]);

  // Gerar dias do calendário
  useEffect(() => {
    const generateCalendarDays = () => {
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);
      
      const firstDayOfWeek = getDay(monthStart);
      
      const prevMonthDays = [];
      for (let i = firstDayOfWeek - 1; i >= 0; i--) {
        const date = new Date(monthStart);
        date.setDate(date.getDate() - (i + 1));
        prevMonthDays.push(date);
      }
      
      const currentMonthDays = eachDayOfInterval({ start: monthStart, end: monthEnd });
      
      const lastDayOfWeek = getDay(monthEnd);
      const nextMonthDays = [];
      for (let i = 1; i <= 6 - lastDayOfWeek; i++) {
        const date = new Date(monthEnd);
        date.setDate(date.getDate() + i);
        nextMonthDays.push(date);
      }
      
      const allDays = [...prevMonthDays, ...currentMonthDays, ...nextMonthDays];
      
      const calendarDays = allDays.map(date => {
        const dayApprovals = approvals.filter(approval => {
          if (!approval.post_date) return false;
          
          const approvalDate = new Date(approval.post_date);
          const adjustedApprovalDate = addDays(approvalDate, 1);
          const calendarDate = new Date(date);
          
          return adjustedApprovalDate.getFullYear() === calendarDate.getFullYear() &&
                 adjustedApprovalDate.getMonth() === calendarDate.getMonth() &&
                 adjustedApprovalDate.getDate() === calendarDate.getDate();
        });
        
        return {
          date,
          isCurrentMonth: isSameMonth(date, currentDate),
          isToday: isToday(date),
          approvals: dayApprovals
        };
      });
      
      setCalendarDays(calendarDays);
    };

    generateCalendarDays();
  }, [currentDate, approvals]);

  const handleApprovalClick = (approval: ApprovalPost) => {
    setSelectedApproval(approval);
    setIsDetailsDialogOpen(true);
  };

  const handleDeleteApproval = async (id: string) => {
    try {
      const { error } = await supabase
        .from('approval_posts')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;

      toast.success('Aprovação excluída com sucesso!');
      
      setApprovals(prev => prev.filter(approval => approval.id !== id));
    } catch (error) {
      console.error('Erro ao excluir aprovação:', error);
      toast.error('Erro ao excluir aprovação');
    }
  };

  const handleRestoreApprovals = () => {
    window.location.reload();
  };

  const loadImageAsBase64 = async (imageUrl: string): Promise<string> => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error loading image:', error);
      return '';
    }
  };

  const generatePDF = async () => {
    try {
      const pdf = new jsPDF('l', 'mm', 'a4');
      
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      const monthYear = format(currentDate, 'MMMM yyyy', { locale: ptBR }).toUpperCase();
      pdf.text(`CALENDÁRIO ${monthYear}`, 20, 30);
      
      const calendarElement = document.getElementById('calendar-container');
      if (!calendarElement) return;
      
      const canvas = await html2canvas(calendarElement, {
        scale: 1.5,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: calendarElement.scrollWidth,
        height: calendarElement.scrollHeight
      });
      
      const imgData = canvas.toDataURL('image/png');
      
      const pageWidth = 297;
      const pageHeight = 210;
      const availableWidth = pageWidth - 40;
      const availableHeight = pageHeight - 70;
      
      const originalWidth = canvas.width;
      const originalHeight = canvas.height;
      const aspectRatio = originalWidth / originalHeight;
      
      let imgWidth = availableWidth;
      let imgHeight = availableWidth / aspectRatio;
      
      if (imgHeight > availableHeight) {
        imgHeight = availableHeight;
        imgWidth = availableHeight * aspectRatio;
      }
      
      const xPosition = (pageWidth - imgWidth) / 2;
      
      pdf.addImage(imgData, 'PNG', xPosition, 50, imgWidth, imgHeight);
      
      const currentMonthApprovals = approvals.filter(approval => {
        if (!approval.post_date) return false;
        const approvalDate = new Date(approval.post_date);
        return isSameMonth(approvalDate, currentDate);
      });
      
      if (currentMonthApprovals.length > 0) {
        for (const approval of currentMonthApprovals) {
          pdf.addPage();
          
          const leftColumnX = 20;
          const rightColumnX = 150;
          const startY = 30;
          
          pdf.setFontSize(20);
          pdf.setFont('helvetica', 'bold');
          pdf.text(approval.title, leftColumnX, startY);
          
          let yPosition = startY + 20;
          
          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'normal');
          
          pdf.text(`Cliente: ${approval.client_name}`, leftColumnX, yPosition);
          yPosition += 10;
          
          const postDate = approval.post_date ? format(new Date(approval.post_date), 'dd/MM/yyyy', { locale: ptBR }) : 'Sem data';
          pdf.text(`Data: ${postDate}`, leftColumnX, yPosition);
          yPosition += 10;
          
          const postType = approval.post_type?.toUpperCase() || 'STATIC';
          pdf.text(`Tipo: ${postType}`, leftColumnX, yPosition);
          yPosition += 10;
          
          const statusColors: { [key: string]: [number, number, number] } = {
            'approved': [0, 128, 0],
            'pending': [255, 165, 0],
            'rejected': [255, 0, 0],
            'archived': [128, 128, 128]
          };
          const color = statusColors[approval.status] || [0, 0, 0];
          pdf.setTextColor(color[0], color[1], color[2]);
          pdf.text(`Status: ${approval.status.toUpperCase()}`, leftColumnX, yPosition);
          pdf.setTextColor(0, 0, 0);
          yPosition += 20;
          
          if (approval.caption) {
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'bold');
            pdf.text('Legenda:', leftColumnX, yPosition);
            yPosition += 8;
            
            pdf.setFont('helvetica', 'normal');
            const maxWidth = 120;
            const lines = pdf.splitTextToSize(approval.caption, maxWidth);
            pdf.text(lines, leftColumnX, yPosition);
          }
          
          let imageUrl = '';
          if (approval.post_type === 'static' && approval.image_url) {
            imageUrl = approval.image_url;
          } else if (approval.post_type === 'carousel' && approval.carousel_images && approval.carousel_images.length > 0) {
            imageUrl = approval.carousel_images[0];
          }
          
          if (imageUrl) {
            try {
              const base64Image = await loadImageAsBase64(imageUrl);
              if (base64Image) {
                const maxImageWidth = 120;
                const maxImageHeight = 160;
                
                const tempImg = new Image();
                tempImg.src = base64Image;
                
                await new Promise((resolve) => {
                  tempImg.onload = resolve;
                });
                
                const originalWidth = tempImg.width;
                const originalHeight = tempImg.height;
                const aspectRatio = originalWidth / originalHeight;
                
                let finalWidth = maxImageWidth;
                let finalHeight = maxImageWidth / aspectRatio;
                
                if (finalHeight > maxImageHeight) {
                  finalHeight = maxImageHeight;
                  finalWidth = maxImageHeight * aspectRatio;
                }
                
                pdf.addImage(base64Image, 'JPEG', rightColumnX, startY + 20, finalWidth, finalHeight);
              }
            } catch (error) {
              console.error('Erro ao carregar imagem para PDF:', error);
              pdf.setFontSize(10);
              pdf.text('Erro ao carregar imagem', rightColumnX, startY + 30);
            }
          }
        }
      }
      
      const fileName = `calendario-${format(currentDate, 'yyyy-MM', { locale: ptBR })}.pdf`;
      pdf.save(fileName);
      
      toast.success('PDF gerado com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      toast.error('Erro ao gerar PDF');
    }
  };

  const goToPreviousMonth = () => {
    setCurrentDate(prevDate => subMonths(prevDate, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(prevDate => addMonths(prevDate, 1));
  };

  const weekDays = isMobile 
    ? ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB']
    : ['DOMINGO', 'SEGUNDA-FEIRA', 'TERÇA-FEIRA', 'QUARTA-FEIRA', 'QUINTA-FEIRA', 'SEXTA-FEIRA', 'SÁBADO'];

  return (
    <div className={cn(
      "container mx-auto py-6",
      isMobile && "px-4 py-4"
    )}>
      {/* Cabeçalho */}
      <div className="mb-6">
        <div className={cn(
          "flex justify-between items-center mb-4",
          isMobile && "flex-col gap-4"
        )}>
          <div className={cn(isMobile && "text-center")}>
            <h1 className={cn(
              "text-4xl font-bold text-black mb-2",
              isMobile && "text-2xl"
            )}>
              {format(currentDate, 'MMMM', { locale: ptBR })}
            </h1>
            <p className={cn(
              "text-2xl text-gray-600",
              isMobile && "text-xl"
            )}>
              {format(currentDate, 'yyyy')}
            </p>
          </div>
          
          <div className={cn(
            "flex items-center gap-4",
            isMobile && "flex-col w-full gap-3"
          )}>
            {/* Navegação de mês - prioritária no mobile */}
            <div className={cn(
              "flex items-center gap-2",
              isMobile && "order-1 w-full justify-center"
            )}>
              <Button 
                variant="outline" 
                size={isMobile ? "sm" : "sm"}
                onClick={goToPreviousMonth}
                className="bg-gray-500 hover:bg-gray-600 text-white border-gray-500"
              >
                <ChevronLeft className={cn("h-4 w-4", isMobile && "mr-1")} />
                {isMobile ? "Ant" : "Mês anterior"}
              </Button>
              <Button 
                variant="outline" 
                size={isMobile ? "sm" : "sm"}
                onClick={goToNextMonth}
                className="bg-gray-500 hover:bg-gray-600 text-white border-gray-500"
              >
                {isMobile ? "Prox" : "Próximo mês"}
                <ChevronRight className={cn("h-4 w-4", isMobile && "ml-1")} />
              </Button>
            </div>

            {/* Seletor de cliente */}
            <Select value={selectedClient} onValueChange={setSelectedClient}>
              <SelectTrigger className={cn(
                "w-48",
                isMobile && "w-full order-2"
              )}>
                <SelectValue placeholder="Selecionar cliente" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os clientes</SelectItem>
                {clients.map(client => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Botões de ação */}
            <div className={cn(
              "flex items-center gap-2",
              isMobile && "order-3 w-full justify-between"
            )}>
              <Button 
                onClick={() => setIsDeletedDialogOpen(true)}
                variant="outline"
                className="text-gray-600 border-gray-600 hover:bg-gray-100"
                size="sm"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {isMobile ? "Restaurar" : "Restaurar Excluídos"}
              </Button>
              
              <Button 
                onClick={generatePDF}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                size="sm"
              >
                <Download className="w-4 h-4 mr-2" />
                {isMobile ? "PDF" : "Gerar PDF"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Calendário */}
      <div id="calendar-container" className={cn(
        "bg-white border border-gray-200",
        isMobile && "border-0 shadow-none"
      )}>
        {/* Cabeçalho dos dias da semana */}
        <div className="grid grid-cols-7 border-b border-gray-200">
          {weekDays.map((day, index) => (
            <div key={index} className={cn(
              "p-3 text-center text-sm font-medium bg-black text-white border-r border-gray-200 last:border-r-0",
              isMobile && "p-2 text-xs"
            )}>
              {day}
            </div>
          ))}
        </div>
        
        {/* Dias do mês */}
        {loading ? (
          <div className="h-96 flex items-center justify-center">
            <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="grid grid-cols-7">
            {calendarDays.map((day, index) => (
              <div 
                key={index} 
                className={cn(
                  "min-h-[120px] p-2 border-r border-b border-gray-200 last:border-r-0",
                  isMobile && "min-h-[80px] p-1",
                  !day.isCurrentMonth ? 'bg-gray-50' : 
                  day.isToday ? 'bg-blue-50' : 'bg-white'
                )}
              >
                <div className={cn(
                  "text-sm font-medium mb-2",
                  isMobile && "text-xs mb-1",
                  day.isToday ? 'text-blue-600 font-bold' : 
                  !day.isCurrentMonth ? 'text-gray-400' : 'text-black'
                )}>
                  {format(day.date, 'd')}
                </div>
                
                {/* Aprovações do dia */}
                <div className={cn(
                  "space-y-1",
                  isMobile && "space-y-0.5"
                )}>
                  {day.approvals.slice(0, isMobile ? 2 : 4).map((approval) => (
                    <div 
                      key={approval.id}
                      className={cn(
                        "text-xs p-1 rounded text-black font-medium cursor-pointer hover:opacity-80 transition-opacity",
                        isMobile && "text-[10px] p-0.5",
                        approval.status === 'approved' ? 'bg-green-400' : 
                        approval.status === 'pending' ? 'bg-yellow-400' : 
                        approval.status === 'rejected' ? 'bg-red-400' : 'bg-gray-400'
                      )}
                      onClick={() => handleApprovalClick(approval)}
                    >
                      <div className="font-bold">
                        {approval.post_type?.toUpperCase()}: 
                      </div>
                      <div className="truncate">
                        {isMobile 
                          ? approval.title.substring(0, 15) + (approval.title.length > 15 ? '...' : '')
                          : approval.title
                        }
                      </div>
                    </div>
                  ))}
                  {isMobile && day.approvals.length > 2 && (
                    <div className="text-[9px] text-gray-500 font-medium">
                      +{day.approvals.length - 2} mais
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Diálogos */}
      <ApprovalDetailsDialog
        approval={selectedApproval}
        isOpen={isDetailsDialogOpen}
        onClose={() => setIsDetailsDialogOpen(false)}
        onDelete={handleDeleteApproval}
      />

      <DeletedApprovalsDialog
        isOpen={isDeletedDialogOpen}
        onClose={() => setIsDeletedDialogOpen(false)}
        onRestore={handleRestoreApprovals}
      />
    </div>
  );
}
